import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import Logo from "../../components/Logo";
import api from "../../utils/api";
import { getSocket } from "../../utils/socket";
import {
  validateEmail,
  validatePassword,
  validatePhone,
  validateFile,
  validateUsername,
  validateRegistrationOtp,
} from "../../utils/validation";
import {
  FaClock,
  FaEye,
  FaEyeSlash,
  FaMapMarkerAlt,
  FaCity,
  FaHome,
  FaTractor,
  FaCreditCard,
  FaCamera,
  FaUpload,
  FaLock,
  FaCheckCircle,
  FaTools,
} from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { useMaintenance } from "../../context/MaintenanceContext";
import RoleIllustration from "../../components/RoleIllustration";
import "./Auth.css";

const Signup = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { maintenance } = useMaintenance();
  const countdownIntervalRef = useRef(null);
  const userTypeRef = useRef(null); // always current role for Google callback (avoids stale closure)
  const [userType, setUserType] = useState(null); // null means no selection yet
  const [hasSelectedType, setHasSelectedType] = useState(false); // Track if user has selected a type
  const [farmerType, setFarmerType] = useState(null); // 'registeredFarm' or 'sellingFromHome' for farmers
  const [formData, setFormData] = useState({
    username: "",
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    city: "",
    district: "",
    street: "",
    province: "",
    municipality: "",
    farmName: "",
    farmAddress: "",
    paymentMethod: "",
    farmPhoto: null,
    idVerification: null,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [farmPhotoPreview, setFarmPhotoPreview] = useState(null);
  const [, setIdPreview] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const [successMessage, setSuccessMessage] = useState("");
  const [, setUserLocation] = useState(null); // [longitude, latitude]
  const [registrationOtp, setRegistrationOtp] = useState("");
  const [lastOtpEmail, setLastOtpEmail] = useState(null);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);

  // Default coordinates (Kathmandu, Nepal)
  const defaultCoordinates = [85.324, 27.7172];

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (otpCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setOtpCooldown((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpCooldown]);

  const nepaleseProvinces = [
    "Province 1",
    "Province 2",
    "Bagmati",
    "Gandaki",
    "Lumbini",
    "Karnali",
    "Sudurpashchim",
  ];

  const districts = {
    "Province 1": [
      "Bhojpur",
      "Dhankuta",
      "Ilam",
      "Jhapa",
      "Khotang",
      "Morang",
      "Okhaldhunga",
      "Panchthar",
      "Sankhuwasabha",
      "Solukhumbu",
      "Sunsari",
      "Taplejung",
      "Terhathum",
      "Udayapur",
    ],
    "Province 2": [
      "Bara",
      "Dhanusha",
      "Mahottari",
      "Parsa",
      "Rautahat",
      "Saptari",
      "Sarlahi",
      "Siraha",
    ],
    Bagmati: [
      "Bhaktapur",
      "Chitwan",
      "Dhading",
      "Dolakha",
      "Kathmandu",
      "Kavrepalanchok",
      "Lalitpur",
      "Makwanpur",
      "Nuwakot",
      "Ramechhap",
      "Rasuwa",
      "Sindhuli",
      "Sindhupalchok",
    ],
    Gandaki: [
      "Baglung",
      "Gorkha",
      "Kaski",
      "Lamjung",
      "Manang",
      "Mustang",
      "Myagdi",
      "Nawalpur",
      "Parbat",
      "Syangja",
      "Tanahun",
    ],
    Lumbini: [
      "Arghakhanchi",
      "Banke",
      "Bardiya",
      "Dang",
      "Gulmi",
      "Kapilvastu",
      "Palpa",
      "Parasi",
      "Pyuthan",
      "Rolpa",
      "Rukum",
      "Rupandehi",
    ],
    Karnali: [
      "Dailekh",
      "Dolpa",
      "Humla",
      "Jajarkot",
      "Jumla",
      "Kalikot",
      "Mugu",
      "Rukum",
      "Salyan",
      "Surkhet",
    ],
    Sudurpashchim: [
      "Achham",
      "Baitadi",
      "Bajhang",
      "Bajura",
      "Dadeldhura",
      "Darchula",
      "Doti",
      "Kailali",
      "Kanchanpur",
    ],
  };

  const handleSendRegistrationOtp = async () => {
    const em = formData.email.trim().toLowerCase();
    setErrors((prev) => ({
      ...prev,
      email: "",
      submit: "",
      registrationOtp: "",
    }));
    if (!validateEmail(em)) {
      setErrors((prev) => ({
        ...prev,
        email: t("signup.validEmail"),
      }));
      return;
    }
    setOtpSending(true);
    try {
      await api.post("/auth/send-registration-otp", { email: em });
      setLastOtpEmail(em);
      setOtpCooldown(60);
      setRegistrationOtp("");
    } catch (err) {
      const msg =
        err.response?.data?.message || t("signup.otpSendFailed");
      const lower = String(msg).toLowerCase();
      if (
        lower.includes("already registered") ||
        lower.includes("already exists")
      ) {
        setErrors((prev) => ({
          ...prev,
          email: t("signup.emailAlreadyRegistered"),
        }));
      } else {
        setErrors((prev) => ({ ...prev, submit: msg }));
      }
    } finally {
      setOtpSending(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "email" && lastOtpEmail) {
      const nextLower = value.trim().toLowerCase();
      if (!nextLower || nextLower !== lastOtpEmail.toLowerCase()) {
        setRegistrationOtp("");
        setLastOtpEmail(null);
      }
    }
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
        ...(name === "province" && { district: "", municipality: "" }),
      };

      return updated;
    });

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validateFile(file);
    if (!validation.isValid) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: validation.error,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [fieldName]: file,
    }));

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (fieldName === "farmPhoto") {
          setFarmPhotoPreview(reader.result);
        } else if (fieldName === "idVerification") {
          setIdPreview(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Username is required.";
    } else {
      const usernameValidation = validateUsername(
        formData.username.toLowerCase(),
      );
      if (!usernameValidation.isValid) {
        newErrors.username = usernameValidation.error;
      }
    }

    if (!formData.name.trim()) {
      newErrors.name = "Full name is required.";
    }

    if (!formData.email) {
      newErrors.email = "Email is required.";
    } else if (!validateEmail(formData.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }

    const emailNorm = formData.email.trim().toLowerCase();
    if (
      !lastOtpEmail ||
      emailNorm !== lastOtpEmail.toLowerCase()
    ) {
      newErrors.email =
        newErrors.email ||
        t("signup.requestVerificationFirst");
    }

    if (!validateRegistrationOtp(registrationOtp)) {
      newErrors.registrationOtp = t("signup.otpRequired");
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required.";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid Nepal phone number (98XXXXXXXX).";
    }

    if (!formData.password) {
      newErrors.password = "Password is required.";
    } else {
      const passwordValidation = validatePassword(formData.password);
      if (!passwordValidation.isValid) {
        newErrors.password = Object.values(passwordValidation.errors)
          .filter(Boolean)
          .join(" ");
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password.";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
    }

    // Address validation for all users
    if (!formData.city.trim()) {
      newErrors.city = "City is required.";
    }
    if (!formData.district.trim()) {
      newErrors.district = "District is required.";
    }
    if (!formData.street.trim()) {
      newErrors.street = "Street address is required.";
    }
    if (!formData.province) {
      newErrors.province = "Province is required.";
    }
    if (!formData.municipality.trim()) {
      newErrors.municipality = "Municipality is required.";
    }

    if (userType === "farmer") {
      if (!farmerType) {
        newErrors.farmerType =
          "Please select whether you have a registered farm or are selling from home.";
      } else if (farmerType === "registeredFarm") {
        // Only validate farm-specific fields if registered farm is selected
        if (!formData.farmName.trim()) {
          newErrors.farmName = "Farm name is required.";
        }
        // farmAddress is optional (additional details only)
        if (!formData.paymentMethod) {
          newErrors.paymentMethod = "Payment method is required.";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to get user's current location
  const getCurrentLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        // Geolocation not supported, use default coordinates
        resolve(defaultCoordinates);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Success: use user's actual location
          const { longitude, latitude } = position.coords;
          resolve([longitude, latitude]);
        },
        (error) => {
          // Error: use default coordinates (Kathmandu, Nepal)
          console.log("Geolocation error:", error.message);
          resolve(defaultCoordinates);
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 60000, // Cache for 1 minute
        },
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setIsLoading(true);

    // Get user location before submitting
    let coordinates;
    try {
      coordinates = await getCurrentLocation();
      setUserLocation(coordinates);
    } catch (error) {
      console.error("Error getting location:", error);
      coordinates = defaultCoordinates;
      setUserLocation(defaultCoordinates);
    }

    try {
      // Create FormData if files are present, otherwise use JSON
      const hasFiles =
        userType === "farmer" &&
        farmerType === "registeredFarm" &&
        formData.farmPhoto;

      let formDataToSend;
      let config = {};

      if (hasFiles) {
        // Use FormData for file uploads
        formDataToSend = new FormData();

        // Basic fields
        formDataToSend.append("username", formData.username.toLowerCase());
        formDataToSend.append("name", formData.name);
        formDataToSend.append("email", formData.email.trim().toLowerCase());
        formDataToSend.append("phone", formData.phone);
        formDataToSend.append("password", formData.password);
        formDataToSend.append("registrationOtp", registrationOtp.trim());
        formDataToSend.append("role", userType);

        // Address fields
        formDataToSend.append("address[city]", formData.city || "");
        formDataToSend.append("address[district]", formData.district || "");
        formDataToSend.append("address[street]", formData.street || "");
        formDataToSend.append("address[province]", formData.province || "");
        formDataToSend.append(
          "address[municipality]",
          formData.municipality || "",
        );

        // Farmer specific fields
        if (userType === "farmer") {
          formDataToSend.append("farmerType", farmerType);

          if (farmerType === "registeredFarm") {
            formDataToSend.append("farmName", formData.farmName);
            formDataToSend.append("paymentMethod", formData.paymentMethod);

            if (formData.farmPhoto) {
              formDataToSend.append("farmPhoto", formData.farmPhoto);
            }
          }
        }

        // Add location coordinates - use simpler format for FormData
        formDataToSend.append("locationLongitude", coordinates[0].toString());
        formDataToSend.append("locationLatitude", coordinates[1].toString());

        // Set headers for FormData (browser will set Content-Type with boundary)
        config = {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        };
      } else {
        // Use JSON for regular form submission
        formDataToSend = {
          username: formData.username.toLowerCase(),
          name: formData.name,
          email: formData.email.trim().toLowerCase(),
          phone: formData.phone,
          password: formData.password,
          registrationOtp: registrationOtp.trim(),
          role: userType,
          address: {
            city: formData.city || "",
            district: formData.district || "",
            street: formData.street || "",
            province: formData.province || "",
            municipality: formData.municipality || "",
          },
        };

        if (userType === "farmer") {
          formDataToSend.farmerType = farmerType;

          if (farmerType === "registeredFarm") {
            formDataToSend.farmName = formData.farmName;
            formDataToSend.paymentMethod = formData.paymentMethod;
          }
        }

        // Add location coordinates
        formDataToSend.location = {
          type: "Point",
          coordinates: coordinates,
        };
      }

      const response = await api.post("/auth/register", formDataToSend, config);

      // Store token
      localStorage.setItem("token", response.data.token);
      localStorage.setItem(
        "user",
        JSON.stringify({
          id: response.data._id,
          username: response.data.username,
          name: response.data.name,
          email: response.data.email,
          role: response.data.role,
          verified: response.data.verified,
          gender: response.data.gender || "",
          dateOfBirth: response.data.dateOfBirth || "",
        }),
      );

      // Show success message
      setIsLoading(false);
      setIsSuccess(true);
      const roleMessage =
        userType === "farmer"
          ? t("signup.farmerSuccess")
          : t("signup.consumerSuccess");
      setSuccessMessage(roleMessage);

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: "smooth" });

      // Start countdown for redirect
      let countdown = 5;
      setRedirectCountdown(countdown);

      countdownIntervalRef.current = setInterval(() => {
        countdown -= 1;
        setRedirectCountdown(countdown);

        if (countdown <= 0) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          // Redirect based on role
          if (userType === "farmer") {
            navigate("/farmer/dashboard");
          } else {
            navigate("/consumer/dashboard");
          }
        }
      }, 1000);
    } catch (error) {
      setIsLoading(false);
      console.error("Registration error:", error);

      const errorMessage =
        error.response?.data?.message ||
        "Registration failed. Please try again.";

      const backendMsg = error.response?.data?.message || "";
      const bmLower = String(backendMsg).toLowerCase();
      if (bmLower.includes("verification") || bmLower.includes("code")) {
        setErrors({
          registrationOtp: backendMsg || t("signup.otpRequired"),
        });
      } else if (
        bmLower.includes("already") &&
        (bmLower.includes("email") ||
          bmLower.includes("registered") ||
          bmLower.includes("exists"))
      ) {
        setErrors({ email: t("signup.emailAlreadyRegistered") });
      } else {
        setErrors({ submit: errorMessage });
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    const selectedRole = userTypeRef.current || userType; // ref is always current when popup closes
    if (!credentialResponse?.credential || !selectedRole) return;
    setIsLoading(true);
    setErrors({});
    try {
      const response = await api.post("/auth/google", {
        credential: credentialResponse.credential,
        role: selectedRole,
      });
      localStorage.setItem("token", response.data.token);
      localStorage.setItem(
        "user",
        JSON.stringify({
          id: response.data._id,
          username: response.data.username,
          name: response.data.name,
          email: response.data.email,
          role: response.data.role,
          verified: response.data.verified,
          profileImage: response.data.profileImage || null,
          gender: response.data.gender || "",
          dateOfBirth: response.data.dateOfBirth || "",
        }),
      );
      window.dispatchEvent(new Event("userUpdated"));
      getSocket();
      const r = response.data.role;
      navigate(r === "farmer" ? "/farmer/dashboard" : "/consumer/dashboard");
    } catch (error) {
      setIsLoading(false);
      const msg = error.response?.data?.message || "Google sign-up failed.";
      setErrors({ submit: msg });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleGoogleError = () => {
    setErrors({ submit: "Google sign-up was cancelled or failed." });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const googleClientId =
    process.env.REACT_APP_GOOGLE_CLIENT_ID ||
    "527730472160-0up0itis0lbgddvdjtciptvh8c7b6nl6.apps.googleusercontent.com";

  if (maintenance) {
    return (
      <div className="auth-container auth-signup-page">
        <div className="auth-signup-wrapper">
          <div className="auth-signup-header">
            <Link to="/">
              <Logo size="small" />
            </Link>
          </div>
          <div className="auth-maintenance-block">
            <div className="auth-maintenance-block-icon-wrap">
              <FaTools className="auth-maintenance-block-icon" />
            </div>
            <h2>{t("maintenance.title", "Under Maintenance")}</h2>
            <p>
              {t(
                "maintenance.signupBlocked",
                "Registration is temporarily unavailable while we perform scheduled maintenance. Please try again later.",
              )}
            </p>
            <Link to="/login" className="auth-maintenance-link">
              {t("maintenance.goToLogin", "Go to Login")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container auth-signup-page">
      <div className="auth-signup-wrapper">
        {/* Header Section */}
        <div className="auth-signup-header">
          <Link to="/">
            <Logo size="small" />
          </Link>
          <h1 className="auth-signup-main-title">
            {!hasSelectedType
              ? t("signup.chooseType")
              : t("signup.createAccount")}
          </h1>
          {hasSelectedType && userType && (
            <button
              type="button"
              className="auth-back-to-selection-btn"
              onClick={() => {
                userTypeRef.current = null;
                setHasSelectedType(false);
                setUserType(null);
                setErrors({});
                setFormData({
                  username: "",
                  name: "",
                  email: "",
                  password: "",
                  confirmPassword: "",
                  phone: "",
                  city: "",
                  district: "",
                  street: "",
                  province: "",
                  municipality: "",
                  farmName: "",
                  farmAddress: "",
                  paymentMethod: "",
                  farmPhoto: null,
                  idVerification: null,
                });
                setFarmPhotoPreview(null);
                setIdPreview(null);
                setFarmerType(null);
                setRegistrationOtp("");
                setLastOtpEmail(null);
                setOtpCooldown(0);
              }}
            >
              {t("signup.changeType")}
            </button>
          )}
        </div>

        {/* User Type Selection - Only show if no type selected */}
        {!hasSelectedType && (
          <div className="auth-user-type-selector">
            <button
              type="button"
              className="auth-user-type-card"
              onClick={() => {
                userTypeRef.current = "consumer";
                setUserType("consumer");
                setHasSelectedType(true);
                setFarmerType(null);
                setErrors({});
              }}
            >
              <div className="auth-user-type-illustration-wrap" aria-hidden>
                <RoleIllustration
                  role="consumer"
                  rotateConsumer
                  size="fill"
                  className="auth-user-type-illustration"
                />
              </div>
              <div className="auth-user-type-content">
                <h3>{t("signup.imConsumer")}</h3>
                <p>{t("signup.consumerDesc")}</p>
              </div>
            </button>

            <button
              type="button"
              className="auth-user-type-card"
              onClick={() => {
                userTypeRef.current = "farmer";
                setUserType("farmer");
                setHasSelectedType(true);
                setErrors({});
              }}
            >
              <div className="auth-user-type-illustration-wrap" aria-hidden>
                <RoleIllustration
                  role="farmer"
                  size="fill"
                  className="auth-user-type-illustration"
                />
              </div>
              <div className="auth-user-type-content">
                <h3>{t("signup.imFarmer")}</h3>
                <p>{t("signup.farmerDesc")}</p>
              </div>
            </button>
          </div>
        )}

        {/* Show form only if user has selected a type */}
        {hasSelectedType && (
          <>
            {/* Verification Banner for Farmers */}
            {userType === "farmer" && (
              <div className="auth-verification-banner-modern">
                <FaLock className="auth-banner-icon-modern" />
                <div className="auth-banner-content">
                  <strong>{t("signup.verificationRequired")}</strong>
                  <p>{t("signup.verificationDesc")}</p>
                </div>
              </div>
            )}

            {/* Success Message */}
            {isSuccess && (
              <div className="auth-success-notification">
                <FaCheckCircle className="auth-success-icon" />
                <div className="auth-success-content">
                  <h3>{t("signup.registrationSuccess")}</h3>
                  <p>{successMessage}</p>
                  <div className="auth-redirect-notice">
                    <FaClock className="auth-redirect-icon" />
                    <span>
                      {t("signup.redirecting", {
                        countdown: redirectCountdown,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Form Card */}
            {!isSuccess && (
              <div className="auth-signup-form-card">
                {googleClientId && (
                  <div className="auth-signup-google-section">
                    <div className="auth-divider auth-signup-divider">
                      <span>OR</span>
                    </div>
                    <div className="auth-google-wrapper auth-signup-google">
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        useOneTap={false}
                        theme="filled_blue"
                        size="large"
                        text="signup_with"
                        shape="rectangular"
                        width="320"
                      />
                      <p className="auth-google-hint">
                        {t("signup.googleHint", {
                          role:
                            userType === "farmer"
                              ? t("common.farmer")
                              : t("common.consumer"),
                        })}
                      </p>
                    </div>
                    <div className="auth-divider auth-signup-divider">
                      <span>{t("signup.orFillForm")}</span>
                    </div>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="auth-signup-form">
                  {errors.submit && (
                    <div className="auth-error-alert">{errors.submit}</div>
                  )}

                  {/* Form Sections - Side by Side */}
                  <div className="auth-form-sections-row">
                    {/* Basic Information Section */}
                    <div className="auth-form-section auth-form-section-left">
                      <h3 className="auth-section-header">
                        {t("signup.basicInfo")}
                      </h3>

                      <div className="auth-form-fields-group">
                        <div className="auth-form-field">
                          <label
                            htmlFor="username"
                            className="auth-field-label"
                          >
                            {t("signup.username")}
                          </label>
                          <input
                            type="text"
                            id="username"
                            name="username"
                            value={formData.username}
                            onChange={(e) => {
                              // Convert to lowercase and remove spaces
                              const value = e.target.value
                                .toLowerCase()
                                .replace(/\s/g, "");
                              setFormData((prev) => ({
                                ...prev,
                                username: value,
                              }));
                              // Clear error if exists
                              if (errors.username) {
                                setErrors((prev) => ({
                                  ...prev,
                                  username: "",
                                }));
                              }
                            }}
                            className={`auth-modern-input ${errors.username ? "error" : ""}`}
                            placeholder="johndoe123"
                            maxLength="30"
                          />
                          {errors.username && (
                            <span className="auth-field-error">
                              {errors.username}
                            </span>
                          )}
                          <small className="auth-field-hint">
                            {t("signup.usernameHint")}
                          </small>
                        </div>

                        <div className="auth-form-field">
                          <label htmlFor="name" className="auth-field-label">
                            {t("signup.fullName")}
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`auth-modern-input ${errors.name ? "error" : ""}`}
                            placeholder="John Doe"
                          />
                          {errors.name && (
                            <span className="auth-field-error">
                              {errors.name}
                            </span>
                          )}
                        </div>

                        <div className="auth-form-field">
                          <label htmlFor="phone" className="auth-field-label">
                            {t("signup.phone")}
                          </label>
                          <div className="auth-phone-input-wrapper">
                            <span className="auth-phone-prefix">+977</span>
                            <input
                              type="tel"
                              id="phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              className={`auth-modern-input ${errors.phone ? "error" : ""}`}
                              placeholder="98XXXXXXXX"
                              maxLength="10"
                            />
                          </div>
                          {errors.phone && (
                            <span className="auth-field-error">
                              {errors.phone}
                            </span>
                          )}
                        </div>

                        <div className="auth-form-field">
                          <label htmlFor="email" className="auth-field-label">
                            {t("signup.email")}
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`auth-modern-input ${errors.email ? "error" : ""}`}
                            placeholder="john@example.com"
                            autoComplete="email"
                          />
                          {errors.email && (
                            <span className="auth-field-error">
                              {errors.email}
                            </span>
                          )}
                        </div>

                        <div className="auth-form-field">
                          <label
                            htmlFor="registrationOtp"
                            className="auth-field-label"
                          >
                            {t("signup.verificationCode")}
                          </label>
                          <div className="auth-otp-row">
                            <input
                              type="text"
                              id="registrationOtp"
                              name="registrationOtp"
                              inputMode="numeric"
                              autoComplete="one-time-code"
                              maxLength={6}
                              value={registrationOtp}
                              onChange={(e) => {
                                const v = e.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 6);
                                setRegistrationOtp(v);
                                if (errors.registrationOtp) {
                                  setErrors((prev) => ({
                                    ...prev,
                                    registrationOtp: "",
                                  }));
                                }
                              }}
                              className={`auth-modern-input auth-otp-input ${errors.registrationOtp ? "error" : ""}`}
                              placeholder={t("signup.otpPlaceholder")}
                            />
                            <button
                              type="button"
                              className="auth-otp-send-btn"
                              disabled={
                                otpSending || otpCooldown > 0 || isLoading
                              }
                              onClick={handleSendRegistrationOtp}
                            >
                              {otpCooldown > 0
                                ? t("signup.resendIn", {
                                    seconds: otpCooldown,
                                  })
                                : otpSending
                                  ? "…"
                                  : t("signup.sendVerificationCode")}
                            </button>
                          </div>
                          {lastOtpEmail && (
                            <small className="auth-field-hint">
                              {t("signup.otpHint", { email: lastOtpEmail })}
                            </small>
                          )}
                          {errors.registrationOtp && (
                            <span className="auth-field-error">
                              {errors.registrationOtp}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Security Section */}
                    <div className="auth-form-section auth-auth-form-section-right">
                      <h3 className="auth-section-header">
                        {t("signup.security")}
                      </h3>

                      <div className="auth-form-fields-group">
                        <div className="auth-form-field">
                          <label
                            htmlFor="password"
                            className="auth-field-label"
                          >
                            {t("signup.password")}
                          </label>
                          <div className="auth-password-field-wrapper">
                            <input
                              type={showPassword ? "text" : "password"}
                              id="password"
                              name="password"
                              value={formData.password}
                              onChange={handleChange}
                              className={`auth-modern-input ${errors.password ? "error" : ""}`}
                              placeholder="Create a strong password"
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              className="auth-password-toggle-btn"
                              onClick={() => setShowPassword(!showPassword)}
                              aria-label={
                                showPassword ? "Hide password" : "Show password"
                              }
                            >
                              {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                          {errors.password && (
                            <span className="auth-field-error">
                              {errors.password}
                            </span>
                          )}
                          <small className="auth-field-hint">
                            {t("signup.passwordHint")}
                          </small>
                        </div>

                        <div className="auth-form-field">
                          <label
                            htmlFor="confirmPassword"
                            className="auth-field-label"
                          >
                            {t("signup.confirmPassword")}
                          </label>
                          <div className="auth-password-field-wrapper">
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              id="confirmPassword"
                              name="confirmPassword"
                              value={formData.confirmPassword}
                              onChange={handleChange}
                              className={`auth-modern-input ${errors.confirmPassword ? "error" : ""}`}
                              placeholder="Re-enter your password"
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              className="auth-password-toggle-btn"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                            >
                              {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>
                          {errors.confirmPassword && (
                            <span className="auth-field-error">
                              {errors.confirmPassword}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Address Section - Show for all users */}
                  <div className="auth-form-section address-section auth-fade-in">
                    <div className="auth-section-divider">
                      <span>{t("signup.addressInfo")}</span>
                    </div>
                    <h3 className="auth-section-header">
                      {t("signup.yourLocation")}
                    </h3>

                    <div className="auth-form-grid">
                      <div className="auth-form-field">
                        <label htmlFor="province" className="auth-field-label">
                          <FaMapMarkerAlt className="label-icon" />
                          {t("signup.province")}
                        </label>
                        <select
                          id="province"
                          name="province"
                          value={formData.province}
                          onChange={handleChange}
                          className={`auth-modern-input auth-modern-select ${errors.province ? "error" : ""}`}
                        >
                          <option value="">{t("signup.selectProvince")}</option>
                          {nepaleseProvinces.map((province) => (
                            <option key={province} value={province}>
                              {province}
                            </option>
                          ))}
                        </select>
                        {errors.province && (
                          <span className="auth-field-error">
                            {errors.province}
                          </span>
                        )}
                      </div>

                      <div className="auth-form-field">
                        <label htmlFor="district" className="auth-field-label">
                          <FaMapMarkerAlt className="label-icon" />
                          {t("signup.district")}
                        </label>
                        <select
                          id="district"
                          name="district"
                          value={formData.district}
                          onChange={handleChange}
                          className={`auth-modern-input auth-modern-select ${errors.district ? "error" : ""}`}
                          disabled={!formData.province}
                        >
                          <option value="">{t("signup.selectDistrict")}</option>
                          {formData.province &&
                            districts[formData.province]?.map((district) => (
                              <option key={district} value={district}>
                                {district}
                              </option>
                            ))}
                        </select>
                        {errors.district && (
                          <span className="auth-field-error">
                            {errors.district}
                          </span>
                        )}
                      </div>

                      <div className="auth-form-field full-width">
                        <label
                          htmlFor="municipality"
                          className="auth-field-label"
                        >
                          <FaCity className="label-icon" />
                          {t("signup.municipality")}
                        </label>
                        <input
                          type="text"
                          id="municipality"
                          name="municipality"
                          value={formData.municipality}
                          onChange={handleChange}
                          className={`auth-modern-input ${errors.municipality ? "error" : ""}`}
                          placeholder="Kathmandu Metropolitan City"
                        />
                        {errors.municipality && (
                          <span className="auth-field-error">
                            {errors.municipality}
                          </span>
                        )}
                      </div>

                      <div className="auth-form-field full-width">
                        <label htmlFor="city" className="auth-field-label">
                          <FaCity className="label-icon" />
                          {t("signup.city")}
                        </label>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          className={`auth-modern-input ${errors.city ? "error" : ""}`}
                          placeholder="Kathmandu"
                        />
                        {errors.city && (
                          <span className="auth-field-error">
                            {errors.city}
                          </span>
                        )}
                      </div>

                      <div className="auth-form-field full-width">
                        <label htmlFor="street" className="auth-field-label">
                          <FaMapMarkerAlt className="label-icon" aria-hidden />
                          {t("signup.streetAddress")}
                        </label>
                        <textarea
                          id="street"
                          name="street"
                          value={formData.street}
                          onChange={handleChange}
                          className={`auth-modern-input auth-modern-textarea ${errors.street ? "error" : ""}`}
                          placeholder="Enter your complete street address"
                          rows="3"
                        />
                        {errors.street && (
                          <span className="auth-field-error">
                            {errors.street}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Farmer Type Selection - Show immediately after address section */}
                  {userType === "farmer" && (
                    <div className="auth-form-section auth-farmer-type-selection auth-fade-in">
                      <div className="auth-section-divider">
                        <span>{t("signup.farmerType")}</span>
                      </div>
                      <h3 className="auth-section-header">
                        {t("signup.howSell")}
                      </h3>
                      <div className="auth-farmer-type-options">
                        <button
                          type="button"
                          className={`auth-farmer-type-card ${farmerType === "registeredFarm" ? "active" : ""}`}
                          onClick={() => {
                            setFarmerType("registeredFarm");
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.farmerType;
                              return newErrors;
                            });
                          }}
                        >
                          <FaTractor className="auth-farmer-type-icon" />
                          <div className="auth-farmer-type-content">
                            <h3>{t("signup.registeredFarm")}</h3>
                            <p>{t("signup.registeredFarmDesc")}</p>
                          </div>
                          {farmerType === "registeredFarm" && (
                            <FaCheckCircle className="auth-check-icon" />
                          )}
                        </button>

                        <button
                          type="button"
                          className={`auth-farmer-type-card ${farmerType === "sellingFromHome" ? "active" : ""}`}
                          onClick={() => {
                            setFarmerType("sellingFromHome");
                            // Clear farm-related data when switching to selling from home
                            setFormData((prev) => ({
                              ...prev,
                              farmName: "",
                              farmAddress: "",
                              paymentMethod: "",
                              farmPhoto: null,
                            }));
                            setFarmPhotoPreview(null);
                            setErrors((prev) => {
                              const newErrors = { ...prev };
                              delete newErrors.farmerType;
                              delete newErrors.farmName;
                              delete newErrors.farmAddress;
                              delete newErrors.paymentMethod;
                              delete newErrors.farmPhoto;
                              return newErrors;
                            });
                          }}
                        >
                          <FaHome className="auth-farmer-type-icon" />
                          <div className="auth-farmer-type-content">
                            <h3>{t("signup.sellingFromHome")}</h3>
                            <p>{t("signup.sellingFromHomeDesc")}</p>
                          </div>
                          {farmerType === "sellingFromHome" && (
                            <FaCheckCircle className="auth-check-icon" />
                          )}
                        </button>
                      </div>
                      {errors.farmerType && (
                        <span
                          className="auth-field-error"
                          style={{
                            display: "block",
                            textAlign: "center",
                            marginTop: "1rem",
                          }}
                        >
                          {errors.farmerType}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Farmer Specific Fields - Only show for registered farms */}
                  {userType === "farmer" && farmerType === "registeredFarm" && (
                    <div className="auth-form-section auth-farmer-section auth-fade-in">
                      <div className="auth-section-divider">
                        <span>{t("signup.farmerDetails")}</span>
                      </div>
                      <h3 className="auth-section-header">
                        {t("signup.farmInfo")}
                      </h3>

                      <div className="auth-form-grid">
                        <div className="auth-form-field full-width">
                          <label
                            htmlFor="farmName"
                            className="auth-field-label"
                          >
                            <FaTractor className="label-icon" />
                            {t("signup.farmName")}
                          </label>
                          <input
                            type="text"
                            id="farmName"
                            name="farmName"
                            value={formData.farmName}
                            onChange={handleChange}
                            className={`auth-modern-input ${errors.farmName ? "error" : ""}`}
                            placeholder="Green Valley Farm"
                          />
                          {errors.farmName && (
                            <span className="auth-field-error">
                              {errors.farmName}
                            </span>
                          )}
                        </div>

                        <div className="auth-form-field full-width">
                          <label
                            htmlFor="farmAddress"
                            className="auth-field-label"
                          >
                            <FaMapMarkerAlt className="label-icon" />
                            {t("signup.farmAddress")}
                          </label>
                          <textarea
                            id="farmAddress"
                            name="farmAddress"
                            value={formData.farmAddress}
                            onChange={handleChange}
                            className={`auth-modern-input auth-modern-textarea ${errors.farmAddress ? "error" : ""}`}
                            placeholder="Enter additional farm address details (e.g., ward number, landmark)"
                            rows="3"
                          />
                          {errors.farmAddress && (
                            <span className="auth-field-error">
                              {errors.farmAddress}
                            </span>
                          )}
                          <small className="auth-field-hint">
                            {t("signup.farmAddressHint")}
                          </small>
                        </div>

                        <div className="auth-form-field full-width">
                          <label
                            htmlFor="paymentMethod"
                            className="auth-field-label"
                          >
                            <FaCreditCard className="label-icon" />
                            {t("signup.paymentMethod")}
                          </label>
                          <select
                            id="paymentMethod"
                            name="paymentMethod"
                            value={formData.paymentMethod}
                            onChange={handleChange}
                            className={`auth-modern-input auth-modern-select ${errors.paymentMethod ? "error" : ""}`}
                          >
                            <option value="">
                              {t("signup.selectPayment")}
                            </option>
                            <option value="Bank">
                              {t("signup.bankTransfer")}
                            </option>
                            <option value="khalti">Khalti</option>
                            <option value="esewa">eSewa</option>
                          </select>
                          {errors.paymentMethod && (
                            <span className="auth-field-error">
                              {errors.paymentMethod}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* File Uploads */}
                      <div className="auth-file-uploads-section">
                        <div className="auth-form-field">
                          <label
                            htmlFor="farmPhoto"
                            className="auth-field-label"
                          >
                            <FaCamera className="label-icon" />
                            {t("signup.farmPhoto")}
                          </label>
                          <div className="auth-file-upload-area">
                            <input
                              type="file"
                              id="farmPhoto"
                              name="farmPhoto"
                              accept="image/png,image/jpeg,image/jpg"
                              onChange={(e) => handleFileChange(e, "farmPhoto")}
                              className="file-input-hidden"
                            />
                            <label
                              htmlFor="farmPhoto"
                              className="auth-file-upload-label"
                            >
                              <FaUpload className="auth-file-upload-icon" />
                              <div className="auth-file-upload-text">
                                <strong>{t("signup.clickUpload")}</strong>{" "}
                                {t("signup.dragDrop")}
                                <span>{t("signup.fileHint")}</span>
                              </div>
                            </label>
                            {farmPhotoPreview && (
                              <div className="auth-file-preview">
                                <img
                                  src={farmPhotoPreview}
                                  alt="Farm preview"
                                />
                                <button
                                  type="button"
                                  className="auth-remove-file-btn"
                                  onClick={() => {
                                    setFarmPhotoPreview(null);
                                    setFormData((prev) => ({
                                      ...prev,
                                      farmPhoto: null,
                                    }));
                                  }}
                                >
                                  <FaXmark />
                                </button>
                              </div>
                            )}
                          </div>
                          {errors.farmPhoto && (
                            <span className="auth-field-error">
                              {errors.farmPhoto}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Submit Button - Show for consumers immediately, for farmers only after farmer type is selected */}
                  {(userType === "consumer" ||
                    (userType === "farmer" && farmerType)) && (
                    <button
                      type="submit"
                      className="auth-submit-button"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner"></span>
                          {t("signup.creatingAccount")}
                        </>
                      ) : (
                        <>
                          <span>{t("signup.createAccountBtn")}</span>
                          <span className="auth-button-arrow">→</span>
                        </>
                      )}
                    </button>
                  )}
                </form>

                {/* Footer */}
                <div className="auth-signup-footer">
                  <p>
                    {t("signup.alreadyAccount")}{" "}
                    <Link to="/login" className="auth-login-link">
                      {t("signup.signIn")}
                    </Link>
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Signup;
